import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, openFile } from "@/lib/api";
import { Eye } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { fmtDateTime, DOC_STATUS, DOC_TYPES } from "@/lib/format";
import { PageHeader, StatusBadge, Empty } from "@/components/kit";
import { DocPreview } from "@/components/DocPreview";

const DocRow = ({ d, onDone }) => {
  const [note, setNote] = useState("");
  const [show, setShow] = useState(d.status === "pending");
  const review = async (action) => {
    try { await api.post(`/admin/documents/${d.id}/review`, { action, note: note || null }); toast.success(action === "approve" ? "Documento aprovado" : "Documento recusado"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-col md:flex-row gap-5 p-5" data-testid={`admin-doc-${d.id}`}>
      {d.original_filename && show && <DocPreview doc={d} />}
      <div className="flex flex-1 flex-col gap-3">
      <div><div className="font-semibold">{d.user_name} <span className="num text-xs text-primary">{d.client_code}</span></div>
        <div className="text-sm">{DOC_TYPES[d.doc_type]} · <span className="num">{d.doc_number}</span>{d.notes && <span className="text-muted-foreground"> · {d.notes}</span>}</div>
        <div className="text-xs text-muted-foreground">{fmtDateTime(d.created_at)}{d.original_filename && ` · ${d.original_filename} (${(d.size / 1024).toFixed(0)} KB)`}</div></div>
      <div className="flex flex-wrap gap-2">
        {d.original_filename && !show && <Button size="sm" variant="outline" className="h-10" onClick={() => setShow(true)} data-testid={`admin-doc-show-preview-${d.id}`}><Eye className="mr-1 h-4 w-4" />Visualizar</Button>}
        {d.original_filename && <Button size="sm" variant="ghost" className="h-10" onClick={() => openFile(`/admin/documents/${d.id}/file`).catch((e) => toast.error(errMsg(e)))} data-testid={`admin-doc-view-${d.id}`}>Abrir em nova aba</Button>}
        {!d.original_filename && <span className="text-xs text-muted-foreground">Sem arquivo anexado</span>}
      </div>
      {d.status === "pending" ? (
        <div className="flex flex-wrap gap-2">
          <Input placeholder="Observação" className="h-10 w-56" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`doc-note-${d.id}`} />
          <Button size="sm" className="h-10" onClick={() => review("approve")} data-testid={`doc-approve-${d.id}`}>Aprovar</Button>
          <Button size="sm" variant="outline" className="h-10" onClick={() => review("reject")} data-testid={`doc-reject-${d.id}`}>Recusar</Button>
        </div>
      ) : <div><StatusBadge map={DOC_STATUS} value={d.status} /></div>}
      </div>
    </div>
  );
};

export default function AdminDocuments() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/documents", { params: { status: status === "all" ? undefined : status } }).then((r) => setRows(r.data));
  usePoll(() => load().catch(() => {}), 20000, [status]);
  return (
    <div data-testid="admin-documents-page">
      <PageHeader eyebrow="Compliance" title="Documentos e solicitações" desc="Analise documentos de identificação enviados pelos clientes." />
      <Tabs value={status} onValueChange={setStatus} className="mb-5"><TabsList>
        {[["pending", "Pendentes"], ["approved", "Aprovados"], ["rejected", "Recusados"], ["all", "Todos"]].map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`docs-tab-${k}`}>{l}</TabsTrigger>)}
      </TabsList></Tabs>
      <div className="surface divide-y divide-border">{rows.length === 0 ? <Empty text="Nenhum documento." /> : rows.map((d) => <DocRow key={d.id} d={d} onDone={load} />)}</div>
    </div>
  );
}
